import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Box, Button, Typography, Stack, IconButton } from '@mui/material';
import {
  PlayArrow,
  ArrowForward,
  VolumeOff,
  PlayCircleOutline,
  Close,
  CheckCircle,
  PlayCircleFilled,
} from '@mui/icons-material';
import { saveVideoProgress } from '../../Services/videoService';

// Wall-clock, not video time, so seeking backwards does not cause a burst of calls.
const PING_INTERVAL_MS = 10000;

// A jump larger than this between timeupdate events is a seek, not playback, so
// it must not count towards time actually watched.
const MAX_REAL_PLAYBACK_STEP = 1.5;

// Treated as "reached the end" — the ended event does not always fire exactly
// at duration, so Next pressed in the last moment still counts as completed.
const END_TOLERANCE = 1;

// Videos always start from 00:00. There is deliberately no resume-from-
// lastPosition seek: a customer who clicked Next part-way through, or left and
// logged back in, is shown the video from the beginning next time. lastPosition
// is still recorded — it is just no longer used to position the player.

// ---------------------------------------------------------------------------
// Information screen copy. Hardcoded by product decision — edit here to change
// what customers read before the series starts.
// ---------------------------------------------------------------------------
const INFO = {
  heading: 'Welcome to your VJ home journey',
  subheading:
    'A short series to walk you through what happens next with your booking.',
  points: [
    'What your booking covers and what comes next',
    'How to read your proposed cost sheet',
    'Your home loan and banking options',
  ],
  footnote: 'You can move to the next video whenever you like.',
};

const LeadVideoModal = ({
  open,
  onClose,
  videos,
  startSequence,
  leadId,
  // false = replay only. Nothing is written to LeadVideoWatchHistory, so a
  // customer revisiting finished videos cannot alter what was recorded the
  // first time round.
  trackProgress = true,
  // true = skip the information screen and play startSequence immediately.
  // Used when the customer picked a specific video on the Tutorials page.
  autoStart = false,
}) => {
  const list = videos || [];

  // -1 = information screen; 0..n-1 = that video
  const [index, setIndex] = useState(-1);
  const [needsUnmute, setNeedsUnmute] = useState(false);

  const videoRef = useRef(null);
  const watchedSincePingRef = useRef(0);
  const lastPingAtRef = useRef(0);
  const prevTimeRef = useRef(0);
  const startedSentRef = useRef(false);
  const completedSentRef = useRef(false);

  const current = index >= 0 ? list[index] : null;
  const isLast = index === list.length - 1;

  // Drives the picker: which rows show as done, and what the button says.
  const watchedCount = list.filter(function (v) { return v.fullyWatched; }).length;
  const pendingCount = list.length - watchedCount;

  useEffect(() => {
    if (!open) return;
    if (autoStart) {
      const at = list.findIndex(function (v) { return v.sequence === startSequence; });
      setIndex(at >= 0 ? at : 0);
      return;
    }
    setIndex(-1);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = useCallback(
    (status) => {
      const el = videoRef.current;
      if (!current || !leadId || !trackProgress) return;

      const delta = watchedSincePingRef.current;
      watchedSincePingRef.current = 0;
      lastPingAtRef.current = Date.now();

      saveVideoProgress({
        leadId,
        videoId: current.id,
        position: el && Number.isFinite(el.currentTime) ? el.currentTime : 0,
        videoDuration: el && Number.isFinite(el.duration) ? el.duration : undefined,
        delta,
        status,
      });
    },
    [current, leadId, trackProgress]
  );

  // Reset per-video accounting and attempt playback whenever the video changes.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || index < 0) return;

    watchedSincePingRef.current = 0;
    lastPingAtRef.current = Date.now();
    prevTimeRef.current = 0;
    startedSentRef.current = false;
    completedSentRef.current = false;
    setNeedsUnmute(false);

    let cancelled = false;

    // Browsers refuse to autoplay audio without enough user activation. The
    // customer pressed a button to get here, but the gesture may not carry, so:
    // try with sound, fall back to muted (always permitted) and offer one tap.
    el.muted = false;
    const attempt = el.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {
        if (cancelled || !videoRef.current) return;
        videoRef.current.muted = true;
        const muted = videoRef.current.play();
        if (muted && typeof muted.catch === 'function') muted.catch(() => {});
        setNeedsUnmute(true);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [index]);

  const handleTimeUpdate = () => {
    const el = videoRef.current;
    if (!el) return;

    const t = el.currentTime;
    const step = t - prevTimeRef.current;
    prevTimeRef.current = t;

    // Only forward movement at roughly real-time speed counts as watching, so
    // dragging the scrubber does not inflate watchedDuration.
    if (step > 0 && step < MAX_REAL_PLAYBACK_STEP) {
      watchedSincePingRef.current += step;
    }

    if (!startedSentRef.current) {
      startedSentRef.current = true;
      send('started');
      return;
    }
    if (Date.now() - lastPingAtRef.current >= PING_INTERVAL_MS) {
      send('started');
    }
  };

  const handleEnded = () => {
    if (completedSentRef.current) return;
    completedSentRef.current = true;
    send('completed');
  };

  // Next (and Close on the last video) ends the customer's time with this
  // video. Which status that is depends on where they were: at the end it is a
  // genuine completion, anywhere earlier it is a skip. Either way the row stops
  // the series reopening; watchPercentage records how much they really saw.
  const handleAdvance = () => {
    const el = videoRef.current;
    const atEnd =
      el &&
      Number.isFinite(el.duration) &&
      el.duration > 0 &&
      el.currentTime >= el.duration - END_TOLERANCE;

    if (!completedSentRef.current) {
      completedSentRef.current = true;
      send(atEnd ? 'completed' : 'skipped');
    }

    if (isLast) {
      onClose();
      return;
    }
    setIndex(index + 1);
  };

  // "Play / Continue" starts at the video the server nominated, falling back to
  // the first one not yet watched. Picking a row directly bypasses this.
  const handleStart = () => {
    let startAt = list.findIndex(function (v) {
      return v.sequence === startSequence;
    });
    if (startAt < 0) startAt = list.findIndex(function (v) { return !v.fullyWatched; });
    setIndex(startAt >= 0 ? startAt : 0);
  };

  if (!list.length) return null;

  return (
    <Modal
      open={open}
      // No backdrop or Escape dismissal: the customer leaves via the buttons.
      onClose={() => {}}
      disableEscapeKeyDown
      // Near-black and blurred, so the page behind reads as switched off and
      // the only thing with any brightness is the video itself.
      // BackdropProps (not slotProps) because this project is on MUI 5.11.
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.88)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
        },
      }}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'auto',
        p: { xs: 1, sm: 2 },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: { xs: '100%', sm: '90vw', md: 960 },
          bgcolor: index < 0 ? 'background.paper' : '#000',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: 24,
          outline: 'none',
        }}
      >
        {index < 0 ? (
          // ---------------- Step 0: information screen ----------------
          <Box sx={{ p: 0 }}>
            <Box
              sx={{
                px: { xs: 3, sm: 6 },
                py: { xs: 4, sm: 6 },
                textAlign: 'center',
                background: 'linear-gradient(135deg, #1570ef, rgba(34, 47, 121, 0.8))',
              }}
            >
              <PlayCircleOutline sx={{ color: '#ff9800', fontSize: 44, mb: 1 }} />
              <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 'bold', color: '#fff', mb: 1.5 }}>
                {INFO.heading}
              </Typography>
              <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', maxWidth: 520, mx: 'auto' }}>
                {INFO.subheading}
              </Typography>
            </Box>

            <Box sx={{ px: { xs: 2.5, sm: 4 }, py: 3 }}>
              {pendingCount > 0 && watchedCount > 0 && (
                <Typography
                  sx={{
                    fontSize: 12.5, fontWeight: 700, color: '#e67e22',
                    fontFamily: 'Inter-Medium', mb: 1.5, textAlign: 'center',
                  }}
                >
                  {pendingCount} of {list.length} still to watch
                </Typography>
              )}

              {/* Each row is its own play button, so the customer can jump
                  straight to whichever video they want. */}
              <Stack spacing={1}>
                {list.map((v, i) => {
                  const done = !!v.fullyWatched;
                  return (
                    <Box
                      key={v.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setIndex(i)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setIndex(i);
                        }
                      }}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        px: 1.5, py: 1.25,
                        borderRadius: 2,
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: done ? '#e6e8eb' : '#f0c9a4',
                        backgroundColor: done ? '#fafbfc' : '#fff8f2',
                        transition: 'border-color .15s, background-color .15s',
                        '&:hover': {
                          borderColor: done ? '#c8cdd3' : '#e67e22',
                          backgroundColor: done ? '#f2f4f6' : '#fdf0e3',
                        },
                        '&:focus-visible': { outline: '2px solid #1570ef', outlineOffset: 2 },
                      }}
                    >
                      {done ? (
                        <CheckCircle sx={{ color: '#2e9e5b', fontSize: 26 }} />
                      ) : (
                        <PlayCircleFilled sx={{ color: '#e67e22', fontSize: 26 }} />
                      )}

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: done ? 500 : 700,
                            color: done ? '#6b7280' : '#1f2937',
                          }}
                        >
                          {i + 1}. {v.title}
                        </Typography>
                        {INFO.points[i] && (
                          <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
                            {INFO.points[i]}
                          </Typography>
                        )}
                      </Box>

                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          fontFamily: 'Inter-Medium',
                          letterSpacing: '0.4px',
                          whiteSpace: 'nowrap',
                          color: done ? '#2e9e5b' : '#e67e22',
                        }}
                      >
                        {done ? 'WATCHED' : 'WATCH'}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>

              <Button
                fullWidth
                onClick={handleStart}
                startIcon={<PlayArrow />}
                disabled={pendingCount === 0}
                sx={{
                  mt: 2.5, backgroundColor: '#e67e22', color: '#fff',
                  p: '12px 24px', fontWeight: 'bold', fontSize: 13,
                  fontFamily: 'Inter-Medium', letterSpacing: '0.9px', borderRadius: 2,
                  '&:hover': { backgroundColor: '#d35400' },
                  '&.Mui-disabled': { backgroundColor: '#e6e8eb', color: '#9aa1a8' },
                }}
              >
                {pendingCount === 0
                  ? 'All videos watched'
                  : watchedCount > 0
                  ? 'Continue watching'
                  : 'Play Videos'}
              </Button>

              <Typography sx={{ fontSize: 12, color: '#777', mt: 2, textAlign: 'center' }}>
                {INFO.footnote}
              </Typography>
            </Box>
          </Box>
        ) : (
          // ---------------- Steps 1..n: the videos ----------------
          <Box sx={{ lineHeight: 0 }}>
            {/* Position counter, top-left so it never collides with the close X */}
            <Box
              sx={{
                position: 'absolute', top: 8, left: 8, zIndex: 2,
                px: 1.5, py: 0.5, borderRadius: 1,
                backgroundColor: 'rgba(0,0,0,0.55)', color: '#fff',
                fontSize: 12, fontFamily: 'Inter-Medium', lineHeight: 1.8,
                maxWidth: 'calc(100% - 70px)', overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {index + 1} / {list.length} &nbsp;·&nbsp; {current.title}
            </Box>

            {/* Close lives in the corner, and only on the final video */}
            {isLast && (
              <IconButton
                onClick={handleAdvance}
                aria-label="Close"
                sx={{
                  position: 'absolute', top: 8, right: 8, zIndex: 2,
                  color: '#fff', backgroundColor: 'rgba(0,0,0,0.55)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            )}

            {needsUnmute && (
              <Button
                onClick={() => {
                  const el = videoRef.current;
                  if (!el) return;
                  el.muted = false;
                  const p = el.play();
                  if (p && typeof p.catch === 'function') p.catch(() => {});
                  setNeedsUnmute(false);
                }}
                startIcon={<VolumeOff />}
                sx={{
                  // Sits clear of the native controls along the bottom edge.
                  position: 'absolute', bottom: { xs: 52, sm: 64 }, left: 8, zIndex: 3,
                  color: '#fff', backgroundColor: 'rgba(0,0,0,0.55)',
                  textTransform: 'none', fontSize: 12, fontFamily: 'Inter-Medium', px: 1.5,
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
                }}
              >
                Tap for sound
              </Button>
            )}

            <video
              ref={videoRef}
              key={current.id}
              src={current.videoUrl}
              controls
              playsInline
              autoPlay
              preload="auto"
              controlsList="nodownload noplaybackrate"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleEnded}
              style={{
                width: '100%',
                maxHeight: '85vh',
                display: 'block',
                backgroundColor: '#000',
              }}
            />

            {/* Next sits ON the video, flush to the right edge and just above
                the native controls - the YouTube "Skip ad" placement. No bar
                below the player, so the video fills the whole modal. */}
            {!isLast && (
              <Button
                onClick={handleAdvance}
                endIcon={<ArrowForward sx={{ fontSize: 18 }} />}
                sx={{
                  position: 'absolute',
                  right: 0,
                  bottom: { xs: 52, sm: 64 },
                  zIndex: 3,
                  minWidth: 0,
                  px: { xs: 1.75, sm: 2.5 },
                  py: { xs: 0.75, sm: 1 },
                  color: '#fff',
                  backgroundColor: 'rgba(0,0,0,0.65)',
                  border: '1px solid rgba(255,255,255,0.7)',
                  borderRight: 'none',
                  // Square against the right edge, rounded on the inner side.
                  borderRadius: '3px 0 0 3px',
                  textTransform: 'none',
                  fontSize: { xs: 13, sm: 14 },
                  fontFamily: 'Inter-Medium',
                  letterSpacing: '0.3px',
                  lineHeight: 1.4,
                  backdropFilter: 'blur(2px)',
                  '& .MuiButton-endIcon': { ml: 0.75 },
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.85)',
                    border: '1px solid #fff',
                    borderRight: 'none',
                  },
                }}
              >
                Next
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default LeadVideoModal;
